import { useState } from 'react';
import { Submission } from '@repo/api/open-calls/graphql';
import {
  AMERICAN_STYLE_EXPERIENCE_BRIEF_QUESTION_MAP,
  BIO_CHAR_LIMIT,
  BIO_EXPAND_BUTTON_TEXT,
  BIO_EXPAND_TEST_ID,
} from './constants';

type ExperienceBriefQA = {
  question: string;
  answer: string;
};

function parseExperienceBriefAsQA(
  experienceBrief: string,
): ExperienceBriefQA[] | null {
  try {
    const parsed = JSON.parse(experienceBrief) as Record<string, string>;
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
      return null;
    }

    const entries = Object.entries(parsed)
      .map(([question, answer]) => ({
        question: question.trim(),
        answer: String(answer ?? '').trim(),
      }))
      .filter((entry) => entry.question && entry.answer);

    return entries.length ? entries : null;
  } catch {
    return null;
  }
}

function mapAmericanStyleExperienceBriefQuestion(question: string): string {
  const key = question.trim();
  return AMERICAN_STYLE_EXPERIENCE_BRIEF_QUESTION_MAP[key] ?? key;
}

type CandidateInfoBioProps = {
  candidate: Submission;
  isAmericanStyleOpenCall?: boolean;
  parseBioAsQa?: boolean;
};

export function CandidateInfoBio({
  candidate,
  isAmericanStyleOpenCall = false,
  parseBioAsQa,
}: CandidateInfoBioProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  // TODO (open-call-types): Remove this fallback once candidate data is always typed for this view.
  const bioText = candidate.data.personalDetails?.experienceBrief ?? '';
  const shouldParseBioAsQa = parseBioAsQa ?? isAmericanStyleOpenCall;
  const qaEntries = shouldParseBioAsQa
    ? parseExperienceBriefAsQA(bioText)?.map((entry) => ({
        ...entry,
        question: mapAmericanStyleExperienceBriefQuestion(entry.question),
      }))
    : null;

  const isTruncatable = bioText.length > BIO_CHAR_LIMIT;

  const toggleExpand = () => setIsExpanded(!isExpanded);

  return (
    <div className="bg-white border-b border-zinc-200">
      <div className="px-4 py-6 text-lg font-bold">Bio</div>
      <div className="p-4">
        {qaEntries ? (
          <div className="space-y-4">
            {qaEntries.map((entry, index) => (
              <div key={`${entry.question}-${index}`} className="space-y-1">
                <p className="font-semibold break-words hyphens-auto">
                  {index + 1}. {entry.question}
                </p>
                <p className="whitespace-pre-wrap break-words hyphens-auto">
                  {entry.answer}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="whitespace-pre-wrap break-words hyphens-auto">
            {isTruncatable && !isExpanded
              ? `${bioText.substring(0, BIO_CHAR_LIMIT)}...`
              : bioText}
          </p>
        )}

        <div className="flex w-full justify-end">
          {isTruncatable && !qaEntries && (
            <button
              data-testid={BIO_EXPAND_TEST_ID}
              onClick={toggleExpand}
              className="text-gray-600 hover:text-gray-800 text-sm mt-2 font-medium"
            >
              {isExpanded
                ? BIO_EXPAND_BUTTON_TEXT.SHOW_LESS
                : BIO_EXPAND_BUTTON_TEXT.READ_MORE}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
